// src/pages/academy/AcademyCategoryPlans.jsx
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Pencil,
  Ban,
  X,
  CreditCard,
  Check,
  Loader2,
} from "lucide-react";
import {
  academyCategoriesAPI,
  academySubscriptionAPI,
  getApiError,
} from "../../services/academyService";
import { ACADEMY_ROOT } from "./academyConstants";

// لازم تطابق AcademySubscriptionPlan.PLAN_CHOICES في الباك اند
const PLAN_TYPES = {
  MONTHLY: { label: "Monthly", defaultDays: 30 },
  QUARTERLY: { label: "Quarterly (3 months)", defaultDays: 90 },
};

const emptyForm = {
  plan_type: "MONTHLY",
  price: "",
  duration_days: PLAN_TYPES.MONTHLY.defaultDays,
  description: "",
  is_active: true,
};

export default function AcademyCategoryPlans() {
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [plans, setPlans] = useState([]);
  const [category, setCategory] = useState(null); // { name, is_free }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState(null); // { plan: null | plan }
  const [form, setForm] = useState(emptyForm);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await academySubscriptionAPI.getAllPlansAdmin(categoryId);
      setPlans(data.plans || []);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "Failed to load plans"));
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchPlans();
    academyCategoriesAPI
      .getById(categoryId)
      .then((d) => setCategory(d?.category || d))
      .catch(() => {});
  }, [categoryId, fetchPlans]);

  const backPath = `${ACADEMY_ROOT}/categories/${categoryId}/exams`;
  const isEditing = !!modal?.plan;
  const usedTypes = plans.map((p) => p.plan_type);
  const availableTypes = Object.keys(PLAN_TYPES).filter(
    (t) => !usedTypes.includes(t)
  );

  const openCreate = () => {
    const type = availableTypes[0];
    if (!type) return;
    setForm({
      ...emptyForm,
      plan_type: type,
      duration_days: PLAN_TYPES[type].defaultDays,
    });
    setFormError("");
    setModal({ plan: null });
  };

  const openEdit = (plan) => {
    setForm({
      plan_type: plan.plan_type,
      price: plan.price,
      duration_days: plan.duration_days,
      description: plan.description || "",
      is_active: plan.is_active,
    });
    setFormError("");
    setModal({ plan });
  };

  const handleTypeChange = (type) =>
    setForm((f) => ({
      ...f,
      plan_type: type,
      duration_days: PLAN_TYPES[type].defaultDays,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const price = Number(form.price);
    const days = Number(form.duration_days);
    if (form.price === "" || Number.isNaN(price) || price < 0) {
      setFormError("Enter a valid price");
      return;
    }
    if (!Number.isInteger(days) || days <= 0) {
      setFormError("Duration must be a whole number greater than zero");
      return;
    }

    try {
      setSaving(true);
      if (isEditing) {
        // plan_type مش بيتعدل من الباك اند
        await academySubscriptionAPI.updatePlan(modal.plan.id, {
          price,
          duration_days: days,
          description: form.description,
          is_active: form.is_active,
        });
        setNotice("Plan updated successfully");
      } else {
        await academySubscriptionAPI.createPlan(categoryId, {
          plan_type: form.plan_type,
          price,
          duration_days: days,
          description: form.description,
          is_active: form.is_active,
        });
        setNotice("Plan created successfully");
      }
      setModal(null);
      fetchPlans();
    } catch (err) {
      console.error(err);
      setFormError(getApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // الباك اند بيعطّل بس (الخطة مربوطة باشتراكات بـ PROTECT)
  const handleDeactivate = async (plan) => {
    if (!window.confirm("This plan will be deactivated and hidden from students. Continue?"))
      return;
    try {
      await academySubscriptionAPI.deletePlan(plan.id);
      setNotice("Plan deactivated");
      fetchPlans();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Payment Plans{category?.name ? ` — ${category.name}` : ""}
            </h1>
            <p className="text-sm text-gray-500">
              Subscription prices for this category
            </p>
          </div>
        </div>

        <button
          onClick={openCreate}
          disabled={availableTypes.length === 0}
          title={availableTypes.length === 0 ? "All plan types already exist" : ""}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          Add Plan
        </button>
      </div>

      {category?.is_free && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm">
          This category is marked as <b>free</b>. Plans won't be shown to students
          and manual subscriptions are blocked until it's switched to paid.
        </div>
      )}

      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="font-medium underline">
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchPlans} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {plans.length === 0 && !error ? (
        <div className="text-center py-16 text-gray-400">
          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No plans for this category yet</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add a plan now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all hover:border-indigo-200 ${
                plan.is_active ? "" : "opacity-60"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900">
                  {PLAN_TYPES[plan.plan_type]?.label || plan.name}
                </h3>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    plan.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {plan.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="text-3xl font-bold text-indigo-600 mb-1">
                {plan.price} <span className="text-base font-medium">SAR</span>
              </p>
              <p className="text-sm text-gray-500 mb-2">
                Duration: {plan.duration_days} days
              </p>
              {plan.description && (
                <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
              )}

              <div className="flex gap-2 pt-3 border-t border-gray-100 mt-3">
                <button
                  onClick={() => openEdit(plan)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
                {plan.is_active && (
                  <button
                    onClick={() => handleDeactivate(plan)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    Deactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditing ? "Edit Plan" : "Add Plan"}
              </h2>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                {formError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan type
              </label>
              <select
                value={form.plan_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={isEditing}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
              >
                {(isEditing ? [form.plan_type] : availableTypes).map((t) => (
                  <option key={t} value={t}>
                    {PLAN_TYPES[t]?.label || t}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (SAR)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm({ ...form, duration_days: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                maxLength={200}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
                className="w-4 h-4"
              />
              Active (visible to students)
            </label>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-medium transition-colors"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}